import React, { useState } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  FileText, 
  Eye,
  Bell,
  Lock,
  Users,
  Settings2,
  Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast.success('Settings saved successfully');
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">AI Settings & Governance</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure AI behavior, safety controls, and compliance</p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      
      <Tabs defaultValue="safety">
        <TabsList>
          <TabsTrigger value="safety">Safety Controls</TabsTrigger>
          <TabsTrigger value="disclaimers">Disclaimers</TabsTrigger>
          <TabsTrigger value="consent">Consent Management</TabsTrigger>
          <TabsTrigger value="audit">Audit Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="safety" className="mt-6 space-y-6">
          {/* Emergency Detection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-primary" />
                Emergency Detection
              </CardTitle>
              <CardDescription>Configure automatic detection and response for emergency situations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-detect emergency keywords</Label>
                  <p className="text-sm text-muted-foreground">Automatically flag messages containing emergency terms</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div>
                <Label>Emergency Keywords</Label>
                <Textarea 
                  className="mt-2" 
                  rows={3}
                  defaultValue="chest pain, can't breathe, emergency, severe pain, bleeding, fainted, unconscious"
                />
                <p className="text-xs text-muted-foreground mt-1">Comma-separated list of keywords to detect</p>
              </div>
              
              <div>
                <Label>Emergency Response Message</Label>
                <Textarea 
                  className="mt-2" 
                  rows={3}
                  defaultValue="I understand you may be experiencing an emergency. Please contact emergency services immediately at 999. Would you like me to notify your therapist?"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Scope Limitations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4 text-info" />
                AI Scope Limitations
              </CardTitle>
              <CardDescription>Define what the AI is allowed and not allowed to do</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Check className="w-4 h-4 text-success" />
                    AI CAN
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Explain assigned exercises</li>
                    <li>• Send appointment reminders</li>
                    <li>• Provide general wellness tips</li>
                    <li>• Answer FAQ questions</li>
                    <li>• Collect pain/function scores</li>
                    <li>• Encourage adherence</li>
                  </ul>
                </div>
                <div className="p-4 border border-destructive/30 rounded-lg bg-primary-light/30">
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    AI CANNOT
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>• Diagnose conditions</li>
                    <li>• Prescribe medications</li>
                    <li>• Provide emergency care advice</li>
                    <li>• Modify treatment plans</li>
                    <li>• Give medical opinions</li>
                    <li>• Replace clinical judgment</li>
                  </ul>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto-escalate out-of-scope requests</Label>
                  <p className="text-sm text-muted-foreground">Automatically escalate to human support when AI reaches limits</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
          
          {/* Escalation Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Escalation Thresholds</CardTitle>
              <CardDescription>Configure when AI should escalate to human support</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Pain Score Threshold</Label>
                  <Select defaultValue="7">
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 6, 7, 8, 9].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} or higher</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sentiment Threshold</Label>
                  <Select defaultValue="negative">
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="very_negative">Very Negative Only</SelectItem>
                      <SelectItem value="negative">Negative & Below</SelectItem>
                      <SelectItem value="neutral">Neutral & Below</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="disclaimers" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                AI Disclaimer Messages
              </CardTitle>
              <CardDescription>Configure disclaimers shown to patients and therapists</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Patient Chat Disclaimer</Label>
                <Textarea 
                  className="mt-2" 
                  rows={2}
                  defaultValue="I'm an AI assistant designed to support your recovery journey. I cannot diagnose conditions or provide medical advice. Always consult your therapist for medical concerns."
                />
              </div>
              
              <div>
                <Label>Therapist Copilot Disclaimer</Label>
                <Textarea 
                  className="mt-2" 
                  rows={2}
                  defaultValue="AI suggestions are assistive only — clinical decisions remain with the healthcare professional."
                />
              </div>
              
              <div>
                <Label>AI Recommendation Footer</Label>
                <Textarea 
                  className="mt-2" 
                  rows={2}
                  defaultValue="This is an AI-generated suggestion based on available data. Please verify and use clinical judgment before acting."
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Show disclaimer on every AI response</Label>
                  <p className="text-sm text-muted-foreground">Display footer disclaimer on all AI-generated content</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="consent" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" />
                Patient Consent Settings
              </CardTitle>
              <CardDescription>Manage data usage and AI interaction consent</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Require explicit AI consent</Label>
                  <p className="text-sm text-muted-foreground">Patients must agree to AI interactions before using the system</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allow data use for AI training</Label>
                  <p className="text-sm text-muted-foreground">Use anonymized patient data to improve AI models</p>
                </div>
                <Switch />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Data retention period</Label>
                  <p className="text-sm text-muted-foreground">How long to keep AI conversation logs</p>
                </div>
                <Select defaultValue="90">
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                    <SelectItem value="180">180 days</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="audit" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Audit & Logging
              </CardTitle>
              <CardDescription>Configure audit trail and logging settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Log all AI conversations</Label>
                  <p className="text-sm text-muted-foreground">Store complete conversation history for audit</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Log AI recommendations</Label>
                  <p className="text-sm text-muted-foreground">Track all AI suggestions made to therapists</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Log recommendation actions</Label>
                  <p className="text-sm text-muted-foreground">Track accepted/rejected AI recommendations</p>
                </div>
                <Switch defaultChecked />
              </div>
              
              <Separator />
              
              <div>
                <Label>Audit Log Retention</Label>
                <Select defaultValue="365">
                  <SelectTrigger className="w-full mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="180">6 months</SelectItem>
                    <SelectItem value="365">1 year</SelectItem>
                    <SelectItem value="730">2 years</SelectItem>
                    <SelectItem value="1825">5 years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
